import {useEffect, useMemo, useRef} from 'react';
import _isNil from 'lodash/isNil';

import {EEventSource, EEventType, IPageData} from 'coreClasses/events/IEvents';
import {EGoogleTagManagerEventType} from 'coreClasses/analytics/providers/gtm/IGoogleTagManager';
import {useProducts, useSetUI, useLocation, useCustomerMgr, useProduct, useLocalesMgr} from 'coreHooks';
import {useWishlist, useBasket} from 'brandHooks';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useCategory, useParentCategoryTree} from 'coreHooks/categories';

import useChangeEffect from './useChangeEffect';

const useHandlePageEvents = (pageData: IPageData, pageDependencies: any[] = [], pageRef = null, pageViewCounter?: number) => {
	const {managerRepository} = useGeneralProvider();
	const location = useLocation();
	const basket = useBasket();
	const wishlist = useWishlist();
	const customerMgr = useCustomerMgr();
	const currentLocale = useLocalesMgr().getCurrentLocale();
	const basketProductIds = basket.getBasketProductIds();
	const basketProducts = useProducts(basketProductIds);
	const customer = customerMgr.getCurrentCustomer();
	const isAppInitialized = customer.isInitialized && basket.isReady();
	const isPageLoadedRef = useRef(false);
	const currentPageRef = useRef(pageRef);
	const currentBasketUpdatedRef = useRef(false);
	const currentWishlistLengthRef = useRef(wishlist?.items?.length);
	const currentBasketLengthRef = useRef(basket?.productItemsCount);
	const setUi = useSetUI();
	const isPageInitialized = pageData.isPageInitialized ?? true;
	const currentLocationKey = useRef<string | undefined>(null);

	// A page kept alive (`<Activity>`) and shown again is a new visit: page view again, as when it remounted.
	useEffect(() => () => {
		isPageLoadedRef.current = false;
		currentLocationKey.current = null;
	}, []);

	const isBasketProductsInitialized = basketProductIds.length
		? basketProducts.every((product) => product.isReady() && !('placeholder' in ((product as any)?.type)))
		: !!basket.isReady();

	const product = useProduct(pageData.pageType === EEventSource.PDP && pageData.pageSpecificData?.productSku);
	const category = useCategory(
		(product && product?.getPrimaryCategoryId()) || pageData?.pageSpecificData?.productCategory,
	);
	const parentCategoryTree = useParentCategoryTree(category);

	const categoryPath = useMemo(() => {
		return (
			parentCategoryTree
				?.map((cat) => cat?.getName())
				?.filter(Boolean)
				?.join('>') || undefined
		);
	}, [parentCategoryTree]);

	useEffect(() => {
		if (
			isAppInitialized &&
			isPageInitialized &&
			basket?.orderTotal !== null &&
			isBasketProductsInitialized &&
			currentLocationKey.current !== location.key
		) {
			currentLocationKey.current = location.key;

			if (!isPageLoadedRef.current || currentPageRef?.current !== pageRef) {
				currentPageRef.current = pageRef;
				isPageLoadedRef.current = true;
				setUi.setCounter(pageViewCounter);

				managerRepository.getProductsMgr().getProductsSync(wishlist.items.map((item) => item.productId)).then((products) => {
					managerRepository.getEventsMgr().emit(EEventType.PAGE_VIEW, {
						pageType: pageData.pageType,
						uri: location.pathname,
						basket: basket,
						customer: customer,
						pageSpecificData: {
							...pageData.pageSpecificData,
							categoryPath
						},
						currentLocale,
						pageViewCounter: pageViewCounter,
						wishlist: wishlist,
						wishlistProducts: products,

						//include key only if property exists in pageData from DG, even if undefined
       					...(Object.hasOwn(pageData, 'siteSection') ? {siteSection: pageData.siteSection} : {} ),
					});
				}).then(() => {
					if (typeof pageData.onLoad === 'function') {
						pageData.onLoad();
					}

					if (pageData.pageType === EEventSource.PDP && pageData?.dataLayerObj) {
						managerRepository.getEventsMgr().emit(EEventType.PRODUCT_DETAIL_VIEW, pageData?.dataLayerObj);
					}
				});
			}
		}
	}, [
		isAppInitialized,
		basket?.orderTotal,
		wishlist?.items?.length,
		isPageInitialized,
		isBasketProductsInitialized,
		...pageDependencies
	]);

	useEffect(() => {
		if (currentBasketUpdatedRef?.current && currentBasketLengthRef.current !== basket.productItemsCount) {
			managerRepository.getEventsMgr().emit(EEventType.CART_UPDATED, {
				basket: basket,
				event: EGoogleTagManagerEventType.CART_UPDATED,
			});

			currentBasketLengthRef.current = basket.productItemsCount;
		}

		currentBasketUpdatedRef.current = !!(isAppInitialized && isBasketProductsInitialized);
	}, [isAppInitialized, basket?.orderTotal, isBasketProductsInitialized, basket?.productItems.map(i => i.getProductId()).join('')]);

	useEffect(() => {
		if (
			isAppInitialized &&
			wishlist?.isReady() &&
			currentWishlistLengthRef?.current !== wishlist?.items?.length &&
			customer?.isRegistered
		) {
			if (!_isNil(currentWishlistLengthRef.current)) {
				managerRepository.getProductsMgr()
					.getProductsSync(wishlist.items.map((item) => item.productId)).then((products) =>
						managerRepository.getEventsMgr().emit(EEventType.WISHLIST_UPDATED, {
							wishlist,
							products,
							event: EGoogleTagManagerEventType.WISHLIST_UPDATED,
						})
					);
			}

			currentWishlistLengthRef.current = wishlist?.items?.length;
		}
	}, [isAppInitialized, customer?.isRegistered, wishlist?.items?.length, wishlist?.isReady()]);

	useChangeEffect(() => {
		isPageLoadedRef.current = false;
	}, [currentLocale]);
};

export default useHandlePageEvents;
